import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import QuanteraHeader from "../components/common/Header";
import HeroVideo from "../components/sections/HeroVideo";
import AboutSection from "../components/sections/AboutSection";
import SolutionsSection from "../components/sections/SolutionsSection";
import Solutions2Section from "../components/sections/Solution2Section";
import ApplicationSection from "../components/sections/ApplicationSection";
import PartnerSection from "../components/sections/PartnerSection";
import ContactSection from "../components/sections/ContactSection";
import Footer from "../components/common/Footer";
import EcosystemSection from "../components/sections/EcosystemSection";

import CookiePreferencesModal from "../components/cookies/CookiePreferencesModal";
import Cookies from "../components/cookies/cookies";
import CookieFloatingButton from "../components/cookies/CookieFloatingButton";
import ChatWidget from "../components/chat/ChatWidget";
import ZaloFloatingButton from "../components/chat/ZaloFloatingButton";
import {
  setAccessLangCookie,
  applyCookiePreferences,
  getCookiePreferences,
  subscribeCookieConsentChange,
} from "../utils/langCookie";

const DEFAULT_PREFS = {
  functional: false,
  analytics: false,
  marketing: false,
};

const Home = () => {
  const location = useLocation();

  const [showCookies, setShowCookies] = useState(false);
  const [openPreferences, setOpenPreferences] = useState(false);
  const [showCookieButton, setShowCookieButton] = useState(false);

  const { i18n } = useTranslation();

  const lang = location.pathname.startsWith("/vi") ? "vi" : "en";

  const canonicalUrl = "https://quantera.vn" + window.location.pathname;

  // Đổi URL (/en|/vi) hoặc Back trình duyệt → cập nhật i18n + cookie (nếu functional bật)
  useEffect(() => {
    i18n.changeLanguage(lang);
    setAccessLangCookie(lang);
  }, [lang, i18n]);

  useEffect(() => {
    const section = location.hash.replace(/^#/, "");
    if (!section) return;

    const validSections = [
      "about",
      "solutions",
      "solutions2",
      "apps",
      "ecosystem",
      "partners",
      "contact",
    ];
    if (!validSections.includes(section)) return;

    const scrollToSection = () => {
      const el = document.getElementById(section);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    };

    const timer = setTimeout(() => {
      if (!scrollToSection()) {
        setTimeout(scrollToSection, 300);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [location.hash, location.pathname]);

  const [preferences, setPreferences] = useState(DEFAULT_PREFS);

  const syncPreferencesFromCookie = () => {
    const prefs = getCookiePreferences();
    if (!prefs) {
      setShowCookies(true);
      setShowCookieButton(false);
      setPreferences(DEFAULT_PREFS);
      return;
    }
    setPreferences({
      functional: prefs.functional === true,
      analytics: prefs.analytics === true,
      marketing: prefs.marketing === true,
    });
    setShowCookies(false);
    setShowCookieButton(true);
  };

  useEffect(() => {
    syncPreferencesFromCookie();
    return subscribeCookieConsentChange(syncPreferencesFromCookie);
  }, []);

  /** Chỉ áp dụng khi Accept / Reject / Lưu — tránh race khi toggle nhanh */
  const commitPreferences = (prefs) => {
    const next = {
      functional: prefs?.functional === true,
      analytics: prefs?.analytics === true,
      marketing: prefs?.marketing === true,
    };
    setPreferences(next);
    applyCookiePreferences(next, lang);
    setShowCookies(false);
    setShowCookieButton(true);
  };

  const handleAcceptAll = () => {
    commitPreferences({
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    commitPreferences({
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = (draftPrefs) => {
    commitPreferences(draftPrefs || preferences);
    setOpenPreferences(false);
  };

  const handleClosePreferences = () => {
    // Cancel / đóng popup: không áp dụng draft — khôi phục từ cookie đã lưu
    syncPreferencesFromCookie();
    setOpenPreferences(false);
  };

  const ogLocale = lang === "vi" ? "vi_VN" : "en_US";
  const ogLocaleAlt = lang === "vi" ? "en_US" : "vi_VN";

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>Quantera</title>

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta
          name="googlebot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta
          name="bingbot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <link rel="canonical" href={canonicalUrl} />

        <link
          rel="alternate"
          hrefLang="en"
          href="https://quantera.vn/en"
        />
        <link
          rel="alternate"
          hrefLang="vi"
          href="https://quantera.vn/vi"
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://quantera.vn/en"
        />

        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content={ogLocaleAlt} />
        <meta property="og:url" content={canonicalUrl} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Quantera Joint Stock Company",
            url: "https://quantera.vn/",
            logo: "https://quantera.vn/favicon.ico",
          })}
        </script>
      </Helmet>

      <QuanteraHeader lang={lang} />
      <HeroVideo />
      <AboutSection />
      <SolutionsSection />
      <Solutions2Section />
      <ApplicationSection />
      <EcosystemSection />
      <PartnerSection />
      <ContactSection />
      <Footer />

      {showCookies && (
        <Cookies
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onPreferences={() => setOpenPreferences(true)}
        />
      )}

      <CookiePreferencesModal
        open={openPreferences}
        onClose={handleClosePreferences}
        preferences={preferences}
        onSave={handleSavePreferences}
      />

      {showCookieButton && (
        <CookieFloatingButton onClick={() => setOpenPreferences(true)} />
      )}

      <ZaloFloatingButton />
      <ChatWidget lang={lang} />
    </>
  );
};

export default Home;
